<template>
  <!-- doelstelling -->
  <div class="card">
    <div class="print">
      <img class="db-logo" src="../../../assets/db/DB-logo1.svg" />

      <h2 class="title">Een persoonlijke indicatieve berekening, van Doelbeleggen.nl voor u!</h2>
      <p style="line-height: 150%">
        U ontvangt hierbij een persoonlijke indicatieve berekening van Doelbeleggen.nl voor uw
        {{ goalType }} op basis van uw persoonlijke uitganspunten: eenmalige inleg,
        beleggingshorizon en uitkeringsperiode, zoals besproken met uw adviseur
        {{ advisor || 'Team Doelbeleggen' }}.
        <br><br>
        Als deze informatieve berekening u aanspreekt, ontvangt u een definitief beleggingsplan
        van Doelbeleggen.nl. Dit beleggingsplan is gebaseerd op uw doelstellingen,
        risicobereidheid en persoonlijke situatie.
        <template v-if="offerTypeShort === 'uitkerend'">
          <br><br>
            In deze indicatieve berekening wordt uitgegaan van een netto voorbeeldrendement van
            {{ rendement }}%. Periodiek berekenen we de hoogte van uw nieuwe uitkering. Dat doen we
            in ieder geval elk jaar op basis van gerealiseerd resultaat. Dit houdt in dat uw
            uitkeringen jaarlijks kunnen stijgen of dalen<sup>*</sup>. Heeft u liever zekerheid over
          de hoogte van uw uitkering? Kies dan voor garantielijfrente.
        </template>
      </p>
      <div
        class="flex-container"
        style="
          justify-content: space-between;
          margin: 40px 0 40px 0;
          line-height: 150%;
        "
      >
        <div class="flex-33">
          <h2 class="green-title">
            <awesomeIcon icon="check" />
            Maximaal flexibel
          </h2>
          <br />
          <i>
            Binnen het fiscale kader zijn nog ruime mogelijkheden om de uitkering in de toekomst
            aan te passen aan uw gewijzigde wensen zoals bijvoorbeeld de looptijd en de uitkering.
          </i>
        </div>
        <div class="flex-33">
          <h2 class="green-title">
            <awesomeIcon icon="check" />
            Uw geld staat veilig
          </h2>
          <br />
          <i>
            Uw beleggingen en geld worden apart bewaard, zo zijn deze bij een  eventueel
            faillisement namelijk veilig. Doelbeleggen.nl werkt met een beleggersgiro en opent
            daar uw beleggingsrekening.
          </i>
        </div>
        <div class="flex-33">
          <h2 class="green-title">
            <awesomeIcon icon="check" />
            100% bij overlijden
          </h2>
          <br />
          <i>
            Bij overlijden gaan de uitkeringen door naar de erfgenamen uit het testament of de
            wettelijke erfgenamen als er geen testament is.
          </i>
        </div>
      </div>

      <h2 class="title">Uw situatie</h2>
      <p style="line-height: 150%">
        Hieronder ziet u de samenvatting van uw situatie en doelstelling. Het doelvermogen is
        gebaseerd op uw risicoprofiel. Hierover leest u meer op de volgende pagina.
        <br />
      </p>
      <table
        v-if="
          offerTypeShort === 'opbouwend'
          && (Number(role) > 7 || Number(role) < 2 && !atpData.connected)
        "
        class="situation-table"
      >
        <tr>
          <td width="30%">Doelvermogen</td>
          <td>&euro; {{ formatPrice(Number(goalData.doel), true) }},-</td>
          <td width="40%" rowspan="10">
            <scoreWidget v-model="goalData.score" :showNotice="false" />
          </td>
        </tr>
        <tr>
          <td>Eenmalige inleg</td>
          <td>&euro; {{ formatPrice(Number(vermogen), true) }},-</td>
        </tr>
        <tr>
          <td>Inleg per maand</td>
          <td>&euro; {{ formatPrice(Number(goalData.stortingenBedrag), true) }},-</td>
        </tr>
        <tr v-if="persoonsData.geboorteDatum">
          <td>Geboortedatum</td>
          <td>{{ new Date(persoonsData.geboorteDatum).toLocaleDateString() }}</td>
        </tr>
        <tr>
          <td>Huidige leeftijd</td>
          <td>
            {{
              persoonsData.geboorteDatum
                ? calcAge
                : Number(goalData.leeftijd)
            }} jaar
          </td>
        </tr>
        <tr>
          <td>
            {{
              persoonsData.geboorteDatum
                ? 'AOW datum'
                : 'Pensioenleeftijd'
            }}
          </td>
          <td>
            {{
              persoonsData.geboorteDatum
                ? aowDate
                : `${Number(goalData.pensioenLeeftijd)} jaar`
            }}
          </td>
        </tr>
        <tr v-if="goalData.targetDate">
          <td>Richtdatum start uitkering</td>
          <td>{{ new Date(goalData.targetDate).toLocaleDateString() }}</td>
        </tr>
        <tr>
          <td>Looptijd uitkering</td>
          <td>{{ Number(goalData.uitkeringsPeriode) }}</td>
        </tr>
        <tr>
          <td>Bruto uitkering per jaar</td>
          <td>&euro; {{ formatPrice(Number(goalData.doelAanvulling) * 12, true) }},-</td>
        </tr>
        <tr>
          <td>Bruto uitkering per maand</td>
          <td>&euro; {{ formatPrice(Number(goalData.doelAanvulling), true) }},-</td>
        </tr>
      </table>
      <template v-else>
        <div
          class="flex-container"
          style="
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            line-height: 150%;
            padding-top: 20px
          "
        >
          <!-- Eenmalige inleg -->
          <div>
            <h3 class="green-title">
              <awesomeIcon icon="coins" /> Eenmalige inleg
            </h3>
            <br />
            <b>&euro;&nbsp;{{ formatPrice(Number(vermogen), true) }},-</b>
          </div>
          <!-- Inleg per maand -->
          <div v-if="offerTypeShort === 'opbouwend'">
            <h3 class="green-title">
              <awesomeIcon icon="calendar-check"/> Inleg per maand
            </h3>
            <br />
            <b>&euro;&nbsp;{{ formatPrice(Number(goalData.stortingenBedrag), true) }},-</b>
          </div>
          <div v-else>
            <h3 class="green-title">
              <awesomeIcon icon="calendar-check"/>
              1e jaaruitkering {{goalType === 'Netto lijfrente' ? '' : '(bruto)'}}
            </h3>
            <br />
            <b>
              &euro;
              {{
                formatPrice(
                  Number(
                    offerType == 1 || offerType == 4
                    ? charts.chart2[0][2]
                    : offerType != 0 && offerType != 5
                      ? charts.chart1[0][2]
                      : charts.chart2[0][1]
                  ),
                  true,
                )
              }},-
            </b>
          </div>
          <!-- Doelvermogen -->
          <div v-if="offerTypeShort === 'opbouwend'">
            <h3 class="green-title">
              <awesomeIcon icon="chart-line" /> Doelvermogen
            </h3>
            <br />
            <b>&euro;&nbsp;{{ formatPrice(Number(goalData.doel), true) }},-</b>
          </div>
          <!-- Uitkering per periode -->
          <div v-else>
            <h3 class="green-title">
              <awesomeIcon icon="chart-line" /> Frequentie ({{ paymentPeriod.toLowerCase() }})
            </h3>
            <br />
            <b>
              &euro;
              {{
                formatPrice(
                  Number(
                    offerType == 1 || offerType == 4
                    ? charts.chart2[0][2]
                    : offerType != 0 && offerType != 5
                      ? charts.chart1[0][2]
                      : charts.chart2[0][1]
                  ) / paymentPeriodNumber,
                  true,
                )
              }},-
            </b>
          </div>
          <!-- Voorbeeldrendement -->
          <div v-if="offerTypeShort === 'uitkerend'" style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="user"/> Voorbeeldrendement<sup>*</sup>
            </h3>
            <br />
            <b>
              {{ rendement }}%
            </b>
          </div>
          <!-- Geboortedatum -->
          <div style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="birthday-cake" /> Geboortedatum
            </h3>
            <br />
            <b v-if="persoonsData.geboorteDatum">
              {{ new Date(persoonsData.geboorteDatum).toLocaleDateString() }}
              ({{
                persoonsData.geboorteDatum
                  ? calcAge
                  : Number(goalData.leeftijd)
              }} jaar)
            </b>
            <b v-else>
              Niet bekend
            </b>
          </div>
          <!-- Pensioenleeftijd -->
          <div v-if="offerTypeShort === 'opbouwend'" style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="star" />
              {{
                persoonsData.geboorteDatum
                  ? 'AOW datum'
                  : 'Pensioenleeftijd'
              }}
            </h3>
            <br />
            <b>
              {{
                persoonsData.geboorteDatum
                  ? aowDate
                  : `${Number(goalData.pensioenLeeftijd)} jaar`
              }}
            </b>
          </div>
          <!-- Looptijd -->
          <div style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="hourglass-half" />
              Looptijd {{ Number(offerType) === 3 && offerTypeShort === 'uitkerend'
                ? '(circa)'
                : ''
              }}
            </h3>
            <br />
            <b>
              {{ offerTypeShort === 'opbouwend' ? aowDuration : Number(duration) }} jaar
            </b>
          </div>
          <!-- Doelstelling -->
          <div style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="fa-regular fa-circle-dot"/> Doelstelling
            </h3>
            <br />
            <b>{{ goalType }}</b>
          </div>
          <!-- Type uitkering -->
          <div v-if="offerTypeShort === 'uitkerend'" style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="fa-wrench"/> Type uitkering
            </h3>
            <br />
            <b>{{ offerTypeToText }}</b>
          </div>
          <!-- Beleggingsprofiel -->
          <div style="margin-top: 40px">
            <h3 class="green-title">
              <awesomeIcon icon="signal"/> Beleggingsprofiel
            </h3>
            <br />
            <b>Doelbeleggen {{ profile }}</b>
          </div>
        </div>

      </template>
      <br>

    </div>
    <div class="docdate">
      {{today.getDate()}}-{{today.getMonth() + 1}}-{{today.getFullYear()}}
    </div>
    <div class="pagefooter">
      {{
        persoonsData.geslacht
          ? persoonsData.geslacht === 'man' ? 'de heer': 'mevrouw'
          : 'de heer/mevrouw'
      }}
      {{(persoonsData.roepnaam || persoonsData.voornaam) || '' }}
      {{ persoonsData.tussenvoegsel || '' }}
      {{ persoonsData.achternaam || '' }}
      <br />
      Indicatieve berekening {{ goalType }}
    </div>
    <div class="pagenumber"></div>
  </div>
</template>
<script>
import scoreWidget from '../../ui/scoreMeter';

export default {
  components: {
    scoreWidget,
  },
  props: {
    goalData: Object,
    atpData: Object,
    advisor: String,
    rendement: Number,
    duration: {
      type: Number,
      default: 10,
    },
    vermogen: {
      type: Number,
      default: 175000,
    },
    profile: {
      type: String,
      default: 'C',
    },
    offerType: {
      type: Number,
      default: -1,
    },
    nextOfKin: {
      type: String,
      default: 'false',
    },
    paymentPeriod: {
      type: String,
      default: 'per maand',
    },
    charts: {
      type: Object,
    },
    persoonsData: Object,
    today: {
      type: Date,
      default: () => new Date(),
    },
  },
  computed: {
    aowDuration() {
      if (this.persoonsData.geboorteDatum) {
        return this.calcAowAge(
          this.persoonsData.geboorteDatum,
          0,
          true,
        ).getFullYear() - new Date().getFullYear();
      }
      return '-';
    },
    aowDate() {
      return this.calcAowAge(this.persoonsData.geboorteDatum, 0, true).toLocaleDateString();
    },
    calcAge() {
      const today = new Date();
      const birthDate = new Date(this.persoonsData.geboorteDatum);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }
      return age;
    },
    offerTypeToText() {
      if (this.offerType === 0 || this.offerType === 5) {
        return 'Normale aanvang';
      }
      if (this.offerType === 1 || this.offerType === 4) {
        return 'Hoge aanvang';
      }
      if (this.offerType === 2) {
        return 'Vaste looptijd';
      }
      return 'Vaste uitkering'; // offerType === 3
    },
    goalType() {
      if (this.goalData.type === 'gouden-handdruk-opbouwend' || this.goalData.type === 'gouden-handdruk-uitkerend') {
        return this.nextOfKin === 'true' ? 'Gouden handdruk, nabestaande' : 'Gouden handdruk';
      }
      if (this.goalData.type === 'netto-lijfrente-uitkerend') {
        return this.nextOfKin === 'true' ? 'Netto lijfrente, nabestaande' : 'Netto lijfrente';
      }
      return this.nextOfKin === 'true' ? 'Lijfrente, nabestaande' : 'Lijfrente';
    },
    paymentPeriodNumber() {
      let interval = 12;
      if (this.paymentPeriod === 'Per kwartaal') {
        interval = 4;
      } else if (this.paymentPeriod === 'Per halfjaar') {
        interval = 2;
      } else if (this.paymentPeriod === 'Per jaar') {
        interval = 1;
      }
      return interval;
    },
    role() {
      return this.$store.state.role;
    },
    offerTypeShort() {
      if (
        this.goalData.type === 'gouden-handdruk-uitkerend'
        || this.goalData.type === 'netto-lijfrente-uitkerend'
        || (this.goalData.type === 'levensonderhoud' && this.goalData.box === '1')
      ) {
        return 'uitkerend';
      }
      return 'opbouwend';
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "../../../scss/_paper.scss";
  table {
    margin-top: 20px;
  }
  h2 {
    margin-top: 30px;
    &:first-child {
      margin-top: 0;
    }
  }
  .selected-profile {
    border: 1px solid $color-highlight;
    background-color: #A4D966;
  }
  .unselected-profile {
    background-color: #fff;
  }
  .situation-table {
    tr {
      background-color: transparent;
      td:first-of-type {
        font-weight: bold;
      }
    }
  }
</style>
